import type { Session } from '@supabase/supabase-js';

import { supabase } from '@/shared/lib/supabase';
import type { CurrentUser } from '@/shared/types/domain';

/**
 * Única camada que fala com `supabase.auth` e com `profiles`/RPCs de
 * identidade. `features/auth/**` está isento da regra de "só api/ importa
 * supabase" (eslint.config.js) porque sessão é estado transversal — mas o
 * resto do app segue importando só esta camada, nunca `@/shared/lib/supabase`.
 */

/**
 * Pede o código de acesso (OTP de 6 dígitos, `supabase/templates/magic_link.html`).
 * A mesma promessa se resolve com sucesso tanto para e-mail cadastrado quanto
 * não cadastrado — é o backend (`enable_signup = false`) que decide
 * silenciosamente se manda e-mail ou não. Nunca inspecione o retorno para
 * diferenciar os dois casos (regra 6 / ADR-002 / US-02). Mesma função serve
 * pro pedido inicial e pro reenvio — reenviar é só chamar de novo.
 */
export async function requestOtp(email: string): Promise<void> {
  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: { shouldCreateUser: false },
  });
  if (!error) return;

  // "Usuário banido" o GoTrue já engole sozinho: verificado em 2026-08-04,
  // /auth/v1/otp devolve 200 tanto pra banido quanto pra ativo. "Usuário não
  // cadastrado" NÃO é engolido — devolve 422 `otp_disabled` (shouldCreateUser:
  // false + enable_signup=false rejeitam o signup implícito). Sem este catch,
  // essa Promise rejeitaria só nesse caso e criaria o oráculo que a regra 6 /
  // ADR-002 / US-02 proíbe: e-mail cadastrado mostra otpSent, não cadastrado
  // mostraria erro. Erro de rede/rate-limit continua propagando normalmente.
  const { code } = error as { code?: string };
  if (code === 'otp_disabled') return;

  throw error;
}

/** Troca o código de 6 dígitos digitado pela sessão. */
export async function verifyOtp(email: string, token: string): Promise<Session> {
  const { data, error } = await supabase.auth.verifyOtp({ email, token, type: 'email' });
  if (error) throw error;
  if (!data.session) throw new Error('Sessão não retornada após verificar o código.');
  return data.session;
}

export async function getSession(): Promise<Session | null> {
  const { data, error } = await supabase.auth.getSession();
  if (error) throw error;
  return data.session;
}

export function onAuthStateChange(callback: (session: Session | null) => void): () => void {
  const {
    data: { subscription },
  } = supabase.auth.onAuthStateChange((_event, session) => callback(session));
  return () => subscription.unsubscribe();
}

export async function signOut(): Promise<void> {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

/**
 * Pessoa + papéis + escopo do usuário logado (ADR-001), lidos do banco a
 * cada chamada — nunca de claim no token.
 *
 * `null` cobre dois casos que a interface trata da mesma forma (US-04):
 * autenticado no Supabase Auth mas sem linha em `profiles` (cadastro
 * incompleto), ou com `profiles` mas sem nenhum vínculo vigente. Não chama
 * `has_active_membership()` à parte: `my_roles()` já vem vazio nesse caso —
 * checar o array evita uma consulta extra.
 */
export async function fetchCurrentUser(): Promise<CurrentUser | null> {
  const session = await getSession();
  if (!session) return null;

  // O filtro por id é obrigatório, não redundante: `can_read_profile` libera a
  // linha de quem é admin sobre TODAS as pessoas (is_admin()), então um select
  // sem filtro devolve a igreja inteira e `.maybeSingle()` estoura PGRST116.
  // Quem restringe a linha aqui é a consulta; a RLS continua sendo a autoridade.
  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', session.user.id)
    .maybeSingle();
  if (profileError) throw profileError;
  if (!profile) return null;

  const [rolesRes, branchIdsRes, coordinationIdsRes, ministryIdsRes] = await Promise.all([
    supabase.rpc('my_roles'),
    supabase.rpc('my_branch_ids'),
    supabase.rpc('my_coordination_ids'),
    supabase.rpc('my_ministry_ids'),
  ]);
  if (rolesRes.error) throw rolesRes.error;
  if (branchIdsRes.error) throw branchIdsRes.error;
  if (coordinationIdsRes.error) throw coordinationIdsRes.error;
  if (ministryIdsRes.error) throw ministryIdsRes.error;

  const roles = rolesRes.data ?? [];
  if (roles.length === 0) return null; // sem vínculo vigente (US-04)

  return {
    profile,
    roles,
    branch_ids: branchIdsRes.data ?? [],
    coordination_ids: coordinationIdsRes.data ?? [],
    ministry_ids: ministryIdsRes.data ?? [],
  };
}
